/* Bekräftelsedialog enligt DS Del Ib (A9). Frågan i rubriken, konsekvensen i
   texten, Avbryt + primär/röd knapp i foten. Ingen formulärkropp.

   Används normalt inte direkt — anropa kpConfirm() från ./dialogApi. Komponenten
   är exporterad för tester och för det fåtal fall som behöver styra open/onClose
   själva. Bygger på ledningssystemets ConfirmDialog (fokusfälla, Esc i capture-
   fasen med stopPropagation så att en underliggande modal inte också stängs). */
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import useFocusTrap from '../../documents/useFocusTrap';
import { pushEscHandler, hasEscDispatcher } from './dialogApi';
import './Dialog.css';

const DEFAULT_CONFIRM = { primary: 'OK', danger: 'Ta bort', warning: 'Fortsätt' };

/**
 * @param {{ open: boolean, title?: string, message?: string, confirmLabel?: string,
 *           cancelLabel?: string, variant?: 'primary'|'danger'|'warning', requireText?: string,
 *           busy?: boolean, children?: React.ReactNode,
 *           onConfirm: () => void, onCancel: () => void }} props
 * `busy` (A9b): knappar och Esc spärras medan en åtgärd pågår; primärknappen visar "…".
 * `children` (A9b): valfritt extra innehåll (t.ex. ett fält) mellan huvud och fot.
 * `confirmDisabled` (A9b): spärra primärknappen tills anropande kod säger till (t.ex. tomt fält i children).
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel, cancelLabel = 'Avbryt', variant = 'primary',
  requireText, busy = false, confirmDisabled = false, children, onConfirm, onCancel,
}) {
  const ref = useRef(null);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  const [typed, setTyped] = useState('');
  const uid = useId();
  useFocusTrap(ref, open);

  const v = ['danger', 'warning', 'primary'].includes(variant) ? variant : 'primary';
  const locked = (!!requireText && typed !== requireText) || !!confirmDisabled;

  useEffect(() => {
    if (!open) return undefined;
    const cancel = () => { if (!busy) onCancel(); };
    // Normalfallet: DialogProvider äger EN tidig window-capture-lyssnare och skickar Esc hit.
    const unregister = pushEscHandler(cancel);
    if (hasEscDispatcher()) return unregister;
    // Reserv (ingen provider, t.ex. isolerat test): egen lyssnare.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); e.stopPropagation(); e.preventDefault(); cancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { unregister(); window.removeEventListener('keydown', onKey, true); };
  }, [open, busy, onCancel]);

  // Startfokus: Avbryt vid destruktiva frågor (Enter får inte råka radera), annars primärknappen.
  useEffect(() => {
    if (!open) return;
    const el = requireText ? null : (v === 'danger' ? cancelRef.current : confirmRef.current);
    if (el) el.focus();
  }, [open, v, requireText]);

  if (!open) return null;

  return createPortal(
    <div className="kp-dialog-overlay" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className={`kp-dialog kp-dialog--${v}`} role="alertdialog" aria-modal="true"
        aria-labelledby={`${uid}-title`} aria-describedby={message ? `${uid}-desc` : undefined} ref={ref}
        aria-busy={busy || undefined} data-testid="kp-confirm-dialog">
        <div className="kp-dialog-head">
          {v !== 'primary' && (
            <div className={`kp-dialog-badge kp-dialog-badge--${v}`} aria-hidden="true">
              <AlertTriangle size={18} strokeWidth={2} />
            </div>
          )}
          <div className="kp-dialog-headtext">
            <div id={`${uid}-title`} className="kp-dialog-title">{title || 'Är du säker?'}</div>
            {message && <div id={`${uid}-desc`} className="kp-dialog-desc">{message}</div>}
          </div>
          <button type="button" className="kp-dialog-close" onClick={onCancel} disabled={busy} aria-label="Stäng">
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>
        {children && <div className="kp-dialog-body">{children}</div>}
        {requireText && (
          <div className="kp-dialog-body">
            <label className="kp-dialog-label" htmlFor={`${uid}-require`}>
              Skriv <strong>{requireText}</strong> för att bekräfta
            </label>
            <input id={`${uid}-require`} className="kp-dialog-input" type="text" autoComplete="off"
              autoFocus value={typed} onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !locked) { e.preventDefault(); onConfirm(); } }} />
          </div>
        )}
        <div className="kp-dialog-foot">
          <button type="button" className="kp-dialog-btn kp-dialog-btn--cancel" onClick={onCancel} disabled={busy} ref={cancelRef}>
            {cancelLabel}
          </button>
          <button type="button" className={`kp-dialog-btn kp-dialog-btn--${v}`} onClick={onConfirm}
            disabled={locked || busy} ref={confirmRef}>
            {(confirmLabel || DEFAULT_CONFIRM[v]) + (busy ? '…' : '')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
